import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { billApi, landedCostCategoryApi } from '../services/friendlyApi';
import type { BillCreate, BillShipmentLink, BillUpdate, ListBillsParams } from '../types';
import { ACCOUNTS_QUERY_KEY } from './useAccounts';

export const BILLS_QUERY_KEY = 'bills';
export const LANDED_COST_CATEGORIES_QUERY_KEY = 'landed-cost-categories';
export const UNBILLED_RECEIPTS_QUERY_KEY = 'unbilled-receipts';

export function useBills(params?: ListBillsParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [BILLS_QUERY_KEY, params],
    queryFn: () => billApi.list(params),
    placeholderData: keepPreviousData,
    // Opt-in gate: callers that only need bills under a condition (e.g. a dialog
    // that's mounted but closed) pass enabled:false to skip the fetch.
    enabled: options?.enabled ?? true,
  });
}

export function useBill(id: string) {
  return useQuery({
    queryKey: [BILLS_QUERY_KEY, id],
    queryFn: () => billApi.get(id),
    enabled: !!id,
  });
}

export function useCreateBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BillCreate) => billApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

/** P2P-1: build a draft bill from a single confirmed goods receipt (GR CTA). */
export function useCreateBillFromReceipt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (goodsReceiptId: string) => billApi.fromReceipt(goodsReceiptId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNBILLED_RECEIPTS_QUERY_KEY] });
    },
  });
}

/** P2P-1: assemble one draft bill from several confirmed, unbilled receipts. */
export function useCreateBillFromReceipts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (goodsReceiptIds: string[]) => billApi.fromReceipts(goodsReceiptIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNBILLED_RECEIPTS_QUERY_KEY] });
    },
  });
}

/** P2P-1: unbilled confirmed receipts for a vendor (picker source). */
export function useUnbilledReceipts(vendorId: string, companyId?: string, enabled = true) {
  return useQuery({
    queryKey: [UNBILLED_RECEIPTS_QUERY_KEY, vendorId, companyId ?? null],
    queryFn: () => billApi.unbilledReceipts(vendorId, companyId),
    enabled: enabled && !!vendorId,
  });
}

export function useUpdateBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BillUpdate }) =>
      billApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useSubmitBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => billApi.submit(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
    },
  });
}

export function useApproveBill() {
  const queryClient = useQueryClient();

  return useMutation({
    // acceptVariance: the explicit reviewer decision to approve despite an
    // out-of-tolerance three-way-match variance (BILL_THREE_WAY_MATCH_HOLD).
    mutationFn: ({ id, acceptVariance }: { id: string; acceptVariance?: boolean }) =>
      billApi.approve(id, acceptVariance),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useDeleteBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => billApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
    },
  });
}

export function useVoidBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => billApi.void(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useCloneBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => billApi.clone(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
    },
  });
}

/** T20B: link/unlink a bill to an inbound freight shipment + landed-cost category. */
export function useLinkBillShipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BillShipmentLink }) =>
      billApi.linkShipment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
    },
  });
}

/**
 * Invoice-first AP: manually link a DRAFT bill to a PO / goods receipt(s) so it
 * relieves GRNI (rebuilds the draft's lines from the selected receipts).
 */
export function useLinkBillPurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, goodsReceiptIds }: { id: string; goodsReceiptIds: string[] }) =>
      billApi.linkPurchaseOrder(id, goodsReceiptIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNBILLED_RECEIPTS_QUERY_KEY] });
    },
  });
}

/** T20B: list the tenant's landed-cost categories for the bill link picker. */
export function useLandedCostCategories(isActive = true) {
  return useQuery({
    queryKey: [LANDED_COST_CATEGORIES_QUERY_KEY, { is_active: isActive }],
    queryFn: () => landedCostCategoryApi.list({ is_active: isActive }),
    staleTime: 5 * 60 * 1000,
  });
}
